// ─── Emails transaccionales — bienvenida, factura pagada, invitaciones ──────
//
// Estrategia:
//   - Los emails de AUTH (OTP de signup, recuperación de contraseña,
//     cambio de email) los maneja Supabase Auth con templates en
//     supabase/email-templates/. Esto ya está conectado a Resend SMTP.
//   - Los emails NO-auth (bienvenida tras pagar, factura recibida,
//     invitación a empleado) los enviamos vía una Supabase Edge Function
//     que usa Resend API (no SMTP). La Edge Function NO está deployed
//     todavía — las funciones de abajo llaman al endpoint y, si no
//     existe, hacen fallback a un mailto: para que el usuario pueda
//     mandarse el correo manual mientras tanto.
//
// Para activar emails reales:
//   1. Crear Edge Function `send-email` en Supabase con `resend.send()`
//   2. Pegar tu RESEND_API_KEY en los secrets de la Edge Function
//   3. Setear REACT_APP_EMAILS_HABILITADOS=true en Vercel
//
// El payload de la Edge Function debería ser:
//   POST /functions/v1/send-email
//   { to, subject, html, from?, businessId? }

import { supabase } from './supabaseClient';
import {
  templateBienvenida,
  templateFacturaPagada,
  templateInvitacionEmpleado,
  templatePlanActivado,
  templateReferidoGanado,
  templateInvitaYGana,
} from './emailTemplates';
import { templateReciboCliente } from './emailTemplateRecibo';
import { fechaOrden, labelMetodoPago, numeroOrdenCorto, nombreQueAtendio } from '../utils/recibo';
import { formatCOP } from '../utils/formatters';

const IS_DEV = process.env.NODE_ENV !== 'production';
// Habilitado por DEFECTO (la edge function send-email ya está desplegada). Solo
// se apaga si explícitamente se pone REACT_APP_EMAILS_HABILITADOS = 'false'.
const DESHABILITADO = process.env.REACT_APP_EMAILS_HABILITADOS === 'false';

function log(...args) { if (IS_DEV) console.log('[emails]', ...args); }

// Envía un email vía la Edge Function "send-email" de Supabase, usando el SDK
// (supabase.functions.invoke), que adjunta el apikey + el token de sesión
// automáticamente (evita problemas de auth/CORS del fetch manual).
// Devuelve { ok, id?, mailto? } — si falla, devuelve un mailto: como fallback.
async function enviarEmail({ to, subject, html, from }) {
  if (DESHABILITADO) {
    log('emails deshabilitados (REACT_APP_EMAILS_HABILITADOS === "false").', subject);
    return { ok: false, mailto: `mailto:${to}?subject=${encodeURIComponent(subject)}` };
  }

  try {
    const { data, error } = await supabase.functions.invoke('send-email', {
      body: { to, subject, html, from: from ?? 'hola@mezopos.com' },
    });
    if (error) throw error;
    if (data && data.ok === false) throw new Error(data.error || 'send-email respondió ok:false');
    log('email enviado:', data?.id ?? '(sin id)');
    return { ok: true, id: data?.id };
  } catch (e) {
    log('error enviando email:', e);
    return { ok: false, mailto: `mailto:${to}?subject=${encodeURIComponent(subject)}`, error: e?.message };
  }
}

// ─── API pública ─────────────────────────────────────────────────────────────

// Recibo de compra al correo del cliente (pedido del cliente piloto). Lo
// dispara el cajero desde el ticket tras cobrar (CarritoPOS) o desde el
// historial (/ventas).
//
//   to:       correo del cliente (ya validado por quien llama)
//   negocio:  objeto `negocio` del AuthContext (fromNegocio) — de ahí salen
//             nombre/razón social, NIT, dirección, teléfono y pie de factura.
//   orden:    shape de `ordenConfirmada` (ver utils/recibo.js). `fecha` puede
//             venir como Date o string ISO (el historial la trae como string).
//   negocioNombre: compat con la firma anterior — se usa solo si no hay `negocio`.
//
// Todo el HTML sale de templateReciboCliente, que ESCAPA cada texto del
// usuario (nombres de producto, notas, negocio) — antes se interpolaban
// crudos y un producto llamado "<b>x" rompía el correo (o inyectaba markup).
export async function emailTicketCliente({ to, negocio = null, negocioNombre = null, orden }) {
  const neg = negocio ?? (negocioNombre ? { name: negocioNombre } : { name: 'mezo' });
  const nombreNegocio = neg.razonSocial || neg.name || 'mezo';
  const fecha = fechaOrden(orden);
  const fechaStr = fecha.toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })
    + ' · ' + fecha.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });

  const html = templateReciboCliente({
    negocio: neg,
    orden: {
      lineas:      orden?.lineas ?? orden?.items ?? [],
      subtotal:    orden?.subtotal ?? null,
      propina:     orden?.propina ?? null,
      canje:       orden?.canje ?? null,
      total:       orden?.total ?? 0,
      metodoLabel: orden?.metodoNombre ?? labelMetodoPago(orden?.metodo),
      numero:      numeroOrdenCorto(orden),
      mesaNumero:  orden?.mesaNumero ?? null,
      // Nunca un correo: ver nombreQueAtendio. Este recibo lo lee un
      // desconocido y la dirección personal del dueño no tiene por qué ir ahí.
      cajero:      nombreQueAtendio(orden?.cajero),
      fechaStr,
    },
  });

  const total = formatCOP(orden?.total);
  return enviarEmail({ to, subject: `Tu recibo de ${nombreNegocio} · ${total}`, html });
}

// Email tras registro + onboarding completo. Lo llama AuthContext después
// de createNegocio.
export async function emailBienvenida({ to, negocioNombre }) {
  return enviarEmail({
    to,
    subject: `Bienvenido a mezo · ${negocioNombre}`,
    html: templateBienvenida({ negocioNombre }),
  });
}

// Email tras un pago Wompi verificado. Lo llama planesService o un
// webhook de Wompi (cuando lo tengamos).
export async function emailFacturaPagada({ to, negocioNombre, plan, monto, fecha, periodo }) {
  return enviarEmail({
    to,
    subject: `Factura mezo · Plan ${plan} activado`,
    html: templateFacturaPagada({ negocioNombre, plan, monto, fecha, periodo }),
  });
}

// Email cuando se invita a un empleado nuevo. Lo llama Empleados.js
// después de createEmpleado.
export async function emailInvitacionEmpleado(empleado, negocio) {
  if (!empleado?.correo) throw new Error('El empleado no tiene correo configurado');
  return enviarEmail({
    to: empleado.correo,
    subject: `Te invitaron a unirte a ${negocio?.name ?? 'mezo'}`,
    html: templateInvitacionEmpleado({
      empleadoNombre: empleado.nombre ?? empleado.correo,
      negocioNombre:  negocio?.name ?? 'su negocio',
      rol:            empleado.roles?.[0] ?? 'cajero',
    }),
  });
}

// Email cuando un usuario activa demo Pro de 15 días.
export async function emailPlanActivado({ to, negocioNombre, plan, expiraEn }) {
  return enviarEmail({
    to,
    subject: `Tu plan ${plan} está activo · mezo`,
    html: templatePlanActivado({ negocioNombre, plan, expiraEn }),
  });
}

// Recompensa de referido: el referidor ganó 1 mes de Pro gratis.
export async function emailReferidoGanado({ to, negocioNombre, referidoNombre }) {
  return enviarEmail({
    to,
    subject: '🎉 Ganaste un mes gratis de Pro · mezo',
    html: templateReferidoGanado({ negocioNombre, referidoNombre }),
  });
}

// Outbound: promociona el programa de referidos, con el código del negocio.
export async function emailInvitaYGana({ to, codigo }) {
  const link = `https://mezopos.com/registro?ref=${codigo}`;
  const mensajeWhatsapp = encodeURIComponent(
    `Yo cobro con mezo, el POS colombiano 🚀. Regístrate con mi link y ambos ganamos: ${link}`
  );
  return enviarEmail({
    to,
    subject: 'Invita y gana meses gratis de Pro · mezo',
    html: templateInvitaYGana({ codigo, link, mensajeWhatsapp }),
  });
}

// Avisa al equipo (Juanes + TemeT) cuando un negocio solicita la demo/cortesía
// y queda en revisión. Lo llama planesService.solicitarDemo.
const ADMINS_MEZO = ['juaneslizcanom@gmail.com', 'temetcol@gmail.com'];
export async function emailSolicitudAcceso({ negocioNombre, nombre, telefono, motivo }) {
  const html = `
    <div style="font-family:system-ui,sans-serif;max-width:520px;margin:0 auto;color:#1A1612">
      <h2 style="color:#C8903F">Nueva solicitud de demo · mezo</h2>
      <p>El negocio <strong>${negocioNombre ?? '—'}</strong> solicitó la demo Pro y quedó <strong>en revisión</strong>.</p>
      <ul style="line-height:1.7">
        <li><strong>Contacto:</strong> ${nombre || '(no especificado)'}</li>
        <li><strong>WhatsApp:</strong> ${telefono || '(no especificado)'}</li>
        <li><strong>Interés:</strong> ${motivo || '(no especificó)'}</li>
      </ul>
      <p>Apruébala o recházala desde <a href="https://mezopos.com/admin-mezo">el panel TemeT</a>.</p>
    </div>`;
  // Un correo por cada admin (Resend acepta varios "to", pero así es más simple).
  return Promise.all(ADMINS_MEZO.map((to) =>
    enviarEmail({ to, subject: `🔔 Solicitud de demo · ${negocioNombre ?? 'mezo'}`, html })
  ));
}

// Mantengo este stub para no romper código antiguo que lo importaba.
export async function emailReporteDiario(_negocio, _reporte, _email) {
  log('emailReporteDiario aún no implementado');
  return { ok: false };
}
